import React, { useContext, useState } from 'react';
import PropTypes from 'prop-types';
import qs from 'query-string';
import { Button } from 'primereact/button';
import { Panel } from 'primereact/panel';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { useLocation, useParams } from 'react-router-dom';
import Edit from './Edit';
import Competencies from './SectionCompetencies';
import { updateTemplate } from '../../../../../services/apps/SurveyTemplateService';
import { buildEmailTemplatesData, getCommunicationTemplateForSurvey } from '../../../../../utils/TemplateUtil';
import TemplateMetaData from './MetaData';
import { subLanguageList } from '../../../../../utils/LanguageUtil';
import { getEntityListWithAccount } from '../../../../../utils/AppUtil';
import { growlActionSuccess, growlErrors } from '../../../../../utils/NotificationUtil';
import { Gr8piFormattedDate } from '../../../../../components/Gr8piFormattedDateTime';
import { useSelector } from 'react-redux';
import { AccountContext } from '../../../../../components/AccountContext';
import { NotificationContext } from '../../../../../components/GrowlNotifications';
import useSubscriptionExpiredRestrictions from '../../../../../hooks/useSubscriptionExpiredRestrictions';

const messages = defineMessages({
  successNoti: {
    id: 'noti.success',
    defaultMessage: 'Success!',
  },
  edit: { id: 'actions.edit', defaultMessage: 'Edit' },
});

const Detail = ({ data }) => {
  const { templateId } = useParams();
  const location = useLocation();
  const query = qs.parse(location.search);

  const { translatedTags } = useSelector((state) => state.i18n);
  const { formatMessage } = useIntl();
  const currentAccount = useContext(AccountContext);
  const growl = useContext(NotificationContext);
  const { subscriptionIsExpired } = useSubscriptionExpiredRestrictions();

  const [editing, setEditing] = useState(query && query.edit);
  const [saving, setSaving] = useState(false);

  const closeEditing = (resetForm) => {
    resetForm();
    setEditing(false);
  };

  const submitEditData = async (formValues) => {
    setSaving(true);

    const { feedbackTemplateId, inviteTemplateId, reminderTemplateId, thankYouTemplateId } = buildEmailTemplatesData(
      formValues,
    );
    const editData = {
      ...formValues,
      feedbackTemplateId,
      inviteTemplateId,
      reminderTemplateId,
      thankYouTemplateId,
      surveyTemplateId: templateId,
      accountId: currentAccount,
    };
    try {
      await updateTemplate(editData);
      growlActionSuccess(growl, formatMessage(messages.successNoti));
    } catch (errors) {
      growlErrors(growl, errors);
    }

    setEditing(false);
    setSaving(false);
  };

  const { template, surveyCompetencies } = data;
  const { createdBy } = template;
  const author = createdBy ? `${createdBy.firstName} ${createdBy.lastName}(${createdBy.email})` : '';
  const activeLocales = subLanguageList(template.activeLocales, translatedTags);
  const reportActiveLocales = subLanguageList(template.reportActiveLocales, translatedTags);
  const initialValues = {
    title: template.title,
    inviteTemplateId: template.inviteTemplateId ? parseInt(template.inviteTemplateId, 10) : -1,
    thankYouTemplateId: template.thankYouTemplateId ? parseInt(template.thankYouTemplateId, 10) : -1,
    reminderTemplateId: template.reminderTemplateId ? parseInt(template.reminderTemplateId, 10) : -1,
    feedbackTemplateId: template.feedbackTemplateId ? parseInt(template.feedbackTemplateId, 10) : -1,
  };

  const communicationTemplates = getEntityListWithAccount(data, 'communicationTemplates');
  const commuTemplates = getCommunicationTemplateForSurvey(communicationTemplates);

  return (
    <div className="p-grid">
      <div className="p-col-12">
        <div className="brand-detailed-wrapper">
          <Panel>
            <div className="p-grid">
              <div className="p-sm-12 p-md-6">
                <Panel
                  header={
                    <div className="p-grid p-nogutter">
                      <div className="p-col">{template.title}</div>
                      {!editing ? (
                        <div className="p-col-2 align-right">
                          <Button
                            type="button"
                            className="p-button-secondary"
                            icon="fa fa-pencil"
                            onClick={() => setEditing(true)}
                            disabled={subscriptionIsExpired}
                            tooltip={formatMessage(messages.edit)}
                            tooltipOptions={{ position: 'bottom' }}
                          />
                        </div>
                      ) : null}
                    </div>
                  }
                >
                  <div className="p-grid">
                    <div className="p-col-12">
                      <div className="details">
                        <span>
                          <FormattedMessage id="attrs.created.verbose" defaultMessage="Created on" />:
                        </span>
                        <span>
                          <Gr8piFormattedDate dateValue={template.createdOn} />
                        </span>
                      </div>
                      <div className="details">
                        <span>
                          <FormattedMessage id="attrs.created_by" defaultMessage="Created by" />:
                        </span>
                        <span>{author}</span>
                      </div>
                      <div className="details">
                        <span>
                          <FormattedMessage id="attrs.updated.verbose" defaultMessage="Updated on" />:
                        </span>
                        <span>
                          <Gr8piFormattedDate dateValue={template.updatedOn} />
                        </span>
                      </div>
                    </div>
                    <div className="p-col-12">
                      <Edit
                        initialValues={initialValues}
                        editing={editing}
                        closeEditForm={closeEditing}
                        saveData={submitEditData}
                        saving={saving}
                        communications={commuTemplates}
                      />
                      {!editing && <Competencies competencies={surveyCompetencies} selected={template.abilities} />}
                    </div>
                  </div>
                </Panel>
              </div>
              <div className="p-md-6 p-sm-12">
                <TemplateMetaData
                  template={template}
                  activeLocales={activeLocales}
                  reportActiveLocales={reportActiveLocales}
                />
              </div>
            </div>
          </Panel>
        </div>
      </div>
    </div>
  );
};

Detail.propTypes = {
  data: PropTypes.objectOf(PropTypes.any).isRequired,
};

export default Detail;
